/**
 * @internal
 * Internal bar rendering layer used by BarChart. Not part of the public API.
 * Use `BarChart` for all bar chart needs.
 */
import React, { forwardRef, RefObject } from 'react';

import { Comp, Flexbox, Number, Text } from '@redsift/design-system';
import { scaleOrdinal as d3scaleOrdinal, ScaleOrdinal as d3ScaleOrdinal } from 'd3';

import { BarChartBarsProps, ComputedBarProps } from './types';
import { StyledBarChartBars } from './styles';
import { useBarChartContext } from './context';
import { BarDatum } from '../../types';
import { Bar, BarOrientation } from '../Bar';

const COMPONENT_NAME = 'BarChartBars';
const CLASSNAME = 'redsift-barchart-bars';

export const BarChartBars: Comp<BarChartBarsProps, SVGGElement> = forwardRef((props, ref) => {
  const { category = 'default', ...forwardedProps } = props;

  const {
    barProps,
    barRole,
    bars,
    barWidth,
    cache,
    categories,
    colorScale,
    direction,
    disableAnimations,
    format,
    gap,
    getBarAnchorProps,
    hasCategory,
    id,
    isBarSelected,
    labelDecorator,
    onBarClick,
    scaleX,
    scaleY,
    tooltipVariant,
    tooltipDecorator,
    xScaleType,
    regions,
  } = useBarChartContext();

  const filteredBarChartBars = bars.filter((bar) => bar.category === category && bar.value > 0);
  const categoryIndex = categories.indexOf(category);
  const categoryLength = categories.length;

  return (
    <StyledBarChartBars
      key={`${id}-series-${category}`}
      aria-hidden="false"
      role="region"
      {...(hasCategory
        ? {
            'aria-label': format('series-legend', {
              category,
              categoryIndex: categoryIndex + 1,
              categoryLength,
              barLength: filteredBarChartBars.length,
            }),
          }
        : {})}
      {...forwardedProps}
      ref={ref as RefObject<SVGGElement>}
    >
      {filteredBarChartBars.map((bar, index: number) => {
        const to = {
          data: bar,
          category,
        };
        const from = cache.current ? { data: cache.current[index] } : undefined;

        const region = regions?.find((r) => {
          return xScaleType === 'Date' || xScaleType === 'dateString'
            ? new Date(r.from).getTime() <= (bar.key as Date).getTime() &&
                new Date(r.to).getTime() >= (bar.key as Date).getTime()
            : r.from <= bar.key && r.to >= bar.key;
        });

        const groupedBars = bars.filter((b) => {
          return xScaleType === 'Date' || xScaleType === 'dateString'
            ? (b.key as Date).getTime() === (bar.key as Date).getTime()
            : b.key === bar.key;
        });

        return [
          <Bar
            color={
              region && region.colorTheme
                ? (
                    d3scaleOrdinal()
                      .domain(Object.keys(region.colorTheme))
                      .range(Object.values(region.colorTheme)) as d3ScaleOrdinal<string, string>
                  )?.(to.data.category)
                : colorScale?.(to.data.category)!
            }
            data={to as BarDatum}
            dataset={bars}
            groupedData={groupedBars}
            disableAnimations={disableAnimations}
            gap={gap}
            id={`id${id}__bar-${categoryIndex}.${index}`}
            index={index}
            isSelected={Boolean(isBarSelected!(to as BarDatum))}
            key={`bar _${categoryIndex}.${index}`}
            labelDecorator={labelDecorator}
            maxHeight={scaleY(bar.cumulativeValue - bar.value)}
            anchorProps={getBarAnchorProps ? getBarAnchorProps(to) : undefined}
            onClick={onBarClick}
            orientation={BarOrientation.vertical}
            direction={direction}
            previousData={from! as BarDatum}
            scale={scaleY}
            scalePosition={scaleX}
            tooltipVariant={tooltipVariant}
            tooltipDecorator={region && region.tooltipDecorator ? region.tooltipDecorator : tooltipDecorator}
            {...barProps}
            width={barProps?.width && typeof barProps.width === 'number' ? barProps.width : barWidth}
            height={undefined}
            role={barRole}
          />,
        ];
      })}
    </StyledBarChartBars>
  );
});

BarChartBars.className = CLASSNAME;
BarChartBars.displayName = COMPONENT_NAME;

export const BarChartGroupedTooltip: React.FC<{
  dateFormatter: (date: Date) => string;
  categories: string[];
  categoryDecorator?: (category: string) => string;
  categoryColor: (category: string) => string;
  children?: React.ReactNode;
  datum: BarDatum;
  groupedData: ComputedBarProps[] | undefined;
}> = ({ dateFormatter, categories, categoryDecorator, categoryColor, children, datum, groupedData }) => {
  const { format } = useBarChartContext();

  const categoriesWithValue = categories.filter(
    (category) => (groupedData?.find((d) => d.category === category)?.value ?? 0) > 0
  );

  return (
    <Flexbox flexDirection="column" margin="16px" gap="0">
      <Flexbox flexDirection="row" gap="8px">
        <Text fontWeight="700" fontSize="14px">
          {dateFormatter(datum.data.key as Date)}
        </Text>
      </Flexbox>
      <div style={{ width: '100%', height: '1px', backgroundColor: '#858585', margin: '8px 0' }} />
      <Flexbox flexDirection="row" gap="8px">
        <Text fontWeight="400" fontSize="14px">
          {format('total')}
        </Text>
        <Number fontWeight="600" fontSize="14px" value={groupedData?.reduce((acc, d) => acc + d.value, 0) ?? 0} />
      </Flexbox>
      {categoriesWithValue.length > 0 ? (
        <Flexbox flexDirection="column" gap="8px" margin="4px 0">
          {categories.map((category) => {
            const value = groupedData?.find((d) => d.category === category)?.value;
            const color = categoryColor(category);
            return value ? (
              <Flexbox key={category} alignItems="center" gap="8px">
                <div style={{ width: '16px', height: '16px', backgroundColor: color }} />
                <Text fontWeight="400" fontSize="14px" lineHeight="18px">
                  {categoryDecorator ? categoryDecorator(category) : category}
                </Text>
                <Number fontWeight="600" fontSize="14px" lineHeight="18px" value={value} />
              </Flexbox>
            ) : null;
          })}
        </Flexbox>
      ) : null}
      {children}
    </Flexbox>
  );
};
